import { useState, useEffect, useCallback, useRef } from 'react';
import type { StatusPanelSettings } from 'types';
import { createDefaultSettings, DEFAULT_STALE_THRESHOLD_HOURS } from 'types';
import { getSettings, saveSettings as apiSaveSettings } from 'services/api';

const CACHE_KEY = 'statusPanelSettings';
const CACHE_TIMESTAMP_KEY = 'statusPanelSettingsTimestamp';
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes
const DEBOUNCE_DELAY_MS = 500; // Debounce auto-save by 500ms

interface UseSettingsResult {
  settings: StatusPanelSettings;
  isLoading: boolean;
  error: Error | null;
  saveSettings: (newSettings: StatusPanelSettings) => Promise<void>;
  refreshSettings: () => Promise<void>;
}

export interface UseDebouncedSettingsSaveResult {
  isSaving: boolean;
  saveError: Error | null;
  debouncedSave: (newSettings: StatusPanelSettings) => void;
  cancelSave: () => void;
}

/**
 * Ensures all settings properties exist, merging with defaults for backward compatibility.
 */
function normalizeSettings(settings: Partial<StatusPanelSettings>): StatusPanelSettings {
  const defaults = createDefaultSettings();
  return {
    slaTimings: settings.slaTimings ?? defaults.slaTimings,
    soundConfigs: settings.soundConfigs ?? defaults.soundConfigs,
    staleThresholdHours: settings.staleThresholdHours ?? DEFAULT_STALE_THRESHOLD_HOURS,
    notificationRules: settings.notificationRules ?? defaults.notificationRules,
    groupConfigs: settings.groupConfigs ?? defaults.groupConfigs,
    nis2Categories: settings.nis2Categories ?? defaults.nis2Categories,
  };
}

function loadFromCache(): StatusPanelSettings | null {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);

    if (cached && timestamp) {
      const age = Date.now() - parseInt(timestamp, 10);
      if (age < CACHE_TTL_MS) {
        return normalizeSettings(JSON.parse(cached));
      }
    }
  } catch {
    // Cache read failed, proceed to fetch
  }
  return null;
}

function saveToCache(settings: StatusPanelSettings): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(settings));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
  } catch {
    // Cache write failed, ignore
  }
}

function clearCache(): void {
  try {
    localStorage.removeItem(CACHE_KEY);
    localStorage.removeItem(CACHE_TIMESTAMP_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Hook for managing Status Panel settings with browser caching.
 * Settings are fetched from the API and cached in localStorage for 5 minutes.
 * Updates are saved to the API and the local cache is refreshed.
 */
export function useSettings(): UseSettingsResult {
  const cachedOnMount = loadFromCache();
  const [settings, setSettings] = useState<StatusPanelSettings>(
    () => cachedOnMount ?? createDefaultSettings(),
  );
  // If we have cache we can render immediately; otherwise show loading until first fetch.
  const [isLoading, setIsLoading] = useState(cachedOnMount === null);
  const [error, setError] = useState<Error | null>(null);

  /** Fetch from API, persist to state + cache. Optionally surface loading/errors to consumers. */
  const fetchAndStore = useCallback(async (surface: boolean): Promise<void> => {
    if (surface) {
      setIsLoading(true);
      setError(null);
    }
    try {
      const fetched = await getSettings();
      const normalized = normalizeSettings(fetched);
      setSettings(normalized);
      saveToCache(normalized);
    } catch (err) {
      if (surface) {
        setError(err instanceof Error ? err : new Error('Failed to fetch settings'));
      }
    } finally {
      if (surface) {
        setIsLoading(false);
      }
    }
  }, []);

  const saveSettings = useCallback(async (newSettings: StatusPanelSettings) => {
    setIsLoading(true);
    setError(null);

    try {
      await apiSaveSettings(newSettings);
      setSettings(newSettings);
      saveToCache(newSettings);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to save settings'));
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const refreshSettings = useCallback(async () => {
    clearCache();
    await fetchAndStore(true);
  }, [fetchAndStore]);

  // Initial fetch on mount: if we had cache, refresh silently in the background;
  // otherwise show loading state on the foreground fetch.
  useEffect(() => {
    fetchAndStore(cachedOnMount === null);
    // cachedOnMount is captured once via the initial render; re-running on deps is unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchAndStore]);

  return {
    settings,
    isLoading,
    error,
    saveSettings,
    refreshSettings,
  };
}

/**
 * Hook for debounced auto-save of settings.
 * Useful for saving settings as the user makes changes, rather than on explicit save.
 * 
 * Usage:
 *   const { debouncedSave, isSaving, saveError } = useDebouncedSettingsSave(onSave);
 *   
 *   // Call when settings change:
 *   debouncedSave(updatedSettings);
 */
export function useDebouncedSettingsSave(
  onSave: (settings: StatusPanelSettings) => Promise<void>
): UseDebouncedSettingsSaveResult {
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<Error | null>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pendingSettingsRef = useRef<StatusPanelSettings | null>(null);

  const executeSave = useCallback(
    async (settings: StatusPanelSettings) => {
      setIsSaving(true);
      setSaveError(null);

      try {
        await onSave(settings);
      } catch (err) {
        setSaveError(err instanceof Error ? err : new Error('Failed to save settings'));
        console.error('Debounced settings save failed:', err);
      } finally {
        setIsSaving(false);
      }
    },
    [onSave]
  );

  const debouncedSave = useCallback(
    (settings: StatusPanelSettings) => {
      // Store the pending settings
      pendingSettingsRef.current = settings;

      // Clear existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new debounce timer
      debounceTimerRef.current = setTimeout(() => {
        if (pendingSettingsRef.current) {
          executeSave(pendingSettingsRef.current);
        }
      }, DEBOUNCE_DELAY_MS);
    },
    [executeSave]
  );

  const cancelSave = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    pendingSettingsRef.current = null;
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return {
    isSaving,
    saveError,
    debouncedSave,
    cancelSave,
  };
}
